import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../Button';
import { Modal } from '../../Modal';
import { Select } from '../../Select';
import { Datepicker } from '../../Datepicker';
import { useFormContext } from 'react-hook-form';
import { EventCalendarValues } from '..';
import { Radio } from '../../Radio';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Input } from '../../Inputs/Input';
import { eventRules } from '../EventRules';
import 'dayjs/locale/pt-br'; // load on demand
import { IntervalType, RecurrenceTypes } from '../../../services/api.types';
import clsx from 'clsx';
import { ModeCalendar } from '../../../pages/ListManyCalendars';
import { IntervalForm } from '../IntervalForm';
import { translateIntervalType } from '../../../utils/translate-enums';
import { capitalize } from '@mui/material';

export type ModalRecurrenceFormValues = Pick<
  EventCalendarValues,
  | 'startDate'
  | 'startHour'
  | 'endHour'
  | 'weekdays'
  | 'bypassConflict'
  | 'recurrenceType'
  | 'count'
  | 'until'
  | 'intervalNumber'
  | 'intervalType'
>;

type ModalRecurrenceProps = {
  mode: ModeCalendar;
  disabled?: boolean;
};

function chooseLabelDay(key: string) {
  if (key === 'monday') {
    return 'Seg';
  } else if (key === 'tuesday') {
    return 'Ter';
  } else if (key === 'wednesday') {
    return 'Qua';
  } else if (key === 'thursday') {
    return 'Qui';
  } else if (key === 'friday') {
    return 'Sex';
  } else if (key === 'saturday') {
    return 'Sab';
  } else if (key === 'sunday') {
    return 'Dom';
  }
  return undefined;
}

export const ModalRecurrence = ({ mode, disabled }: ModalRecurrenceProps) => {
  const form = useFormContext<ModalRecurrenceFormValues>();
  const [openRecurrence, setOpenRecurrence] = useState(false);
  const until = form.watch('until');
  const recurrenceType = form.watch('recurrenceType');
  const count = form.watch('count');
  const weekdays = form.watch('weekdays');
  const startDate = form.watch('startDate');
  const intervalNumber = form.watch('intervalNumber');
  const intervalType = form.watch('intervalType');

  const label =
    recurrenceType !== RecurrenceTypes.ONCE
      ? 'Valores escolhidos pelo usuário'
      : 'Não se repete';

  const [selectedOption, setSelectedOption] = useState(label);

  const handleOpenRecurrence = (value: string) => {
    const shouldResetFields =
      mode === ModeCalendar.create ||
      form.getValues('recurrenceType') === RecurrenceTypes.ONCE;

    if (value === 'Não se repete') {
      form.resetField('weekdays');
      form.setValue('recurrenceType', RecurrenceTypes.ONCE);
    } else if (value === 'Personalizar recorrência' && shouldResetFields) {
      const selectDay = Number(startDate?.day());
      const days: (keyof EventCalendarValues['weekdays'])[] = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      const resultObject: EventCalendarValues['weekdays'] = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      };
      if (selectDay >= 0) resultObject[days[selectDay]] = true;
      form.setValue('weekdays', resultObject);
      form.setValue('recurrenceType', RecurrenceTypes.NEVER);
      setOpenRecurrence(true);
    } else {
      setOpenRecurrence(true);
    }
    setSelectedOption(value);
  };

  const options = useMemo(() => {
    const options = [
      { value: 'Não se repete', label: 'Não se repete' },
      {
        value: 'Personalizar recorrência',
        label: 'Personalizar recorrência',
      },
    ];

    if (recurrenceType === RecurrenceTypes.ONCE || openRecurrence)
      return options;

    const daysLabels = Object.entries(weekdays)
      .flatMap(([key, value]) => (value ? chooseLabelDay(key) : null))
      .filter((day) => !!day);
    const lastDay = daysLabels.pop();
    const days =
      daysLabels.join(', ') + (daysLabels.length > 0 ? ' e ' : '') + lastDay;

    const intervalPrefix =
      parseInt(intervalNumber) === 1 ? 'todo(a)' : `a cada ${intervalNumber}`;
    const interval = `${intervalPrefix} ${translateIntervalType(intervalType)}`;

    let labelPrefix = interval;
    if (intervalType === IntervalType.WEEKLY) {
      labelPrefix = `${days} ${interval}`;
    } else if (intervalType === IntervalType.MONTHLY) {
      const day = startDate?.date();
      labelPrefix = `${interval} dia ${day}`;
    } else if (intervalType === IntervalType.YEARLY) {
      labelPrefix = `${interval} dia ${startDate
        ?.locale('pt-br')
        .format('D [de] MMMM')}`;
    }

    let endEvent = '';
    if (recurrenceType === RecurrenceTypes.NEVER) {
      endEvent = 'termina nunca';
    } else if (recurrenceType === RecurrenceTypes.COUNT) {
      endEvent = `termina em ${count} ocorrências`;
    } else if (recurrenceType === RecurrenceTypes.UNTIL) {
      endEvent = `termina em ${until?.locale('pt-br').format('ll')}`;
    }

    const label = capitalize(`${labelPrefix} e ${endEvent}`);

    return [
      ...options,
      {
        value: 'Valores escolhidos pelo usuário',
        label: label,
      },
    ];
  }, [
    recurrenceType,
    openRecurrence,
    weekdays,
    intervalNumber,
    intervalType,
    startDate,
    count,
    until,
  ]);

  const isFormValid = () => {
    if (
      intervalType === IntervalType.WEEKLY &&
      !Object.values(weekdays).reduce((acc, current) => acc || current, false)
    )
      return false;
    if (recurrenceType === RecurrenceTypes.UNTIL) {
      const isEventEndValid = until && !form.getFieldState('until').error;
      return isEventEndValid;
    } else if (recurrenceType === RecurrenceTypes.COUNT) {
      const isOccurrenceValid = count && !form.getFieldState('count').error;
      return isOccurrenceValid;
    }
    return true;
  };

  useEffect(() => {
    form.resetField('count');
    form.resetField('until');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurrenceType]);

  return (
    <>
      <Select
        disabled={disabled}
        id="SelectModal"
        label="Se repete?"
        size="small"
        className={clsx(
          'max-w-full text-left font-normal overflow-hidden truncate'
        )}
        value={selectedOption}
        options={options}
        onChange={handleOpenRecurrence}
      />
      {openRecurrence && (
        <Modal
          open={openRecurrence}
          onClose={(_, reason) => {
            if (
              reason &&
              reason !== 'backdropClick' &&
              reason !== 'escapeKeyDown'
            )
              setOpenRecurrence(false);
          }}
        >
          <div className="max-w-lg p-4">
            <h3 className="mt-0">Personalizar recorrência</h3>

            <IntervalForm />

            <Radio
              label="Duração:"
              name="recurrenceType"
              options={[
                {
                  label: 'Sempre',
                  value: RecurrenceTypes.NEVER,
                },
                {
                  label: 'Número específico de vezes',
                  value: RecurrenceTypes.COUNT,
                  labelComponent: (
                    <div className="ml-auto">
                      <Input
                        label="total de ... ocorrências"
                        floatLabel
                        rules={eventRules.occurrenceRequired}
                        disabled={recurrenceType !== RecurrenceTypes.COUNT}
                        type="number"
                        name="count"
                      />
                    </div>
                  ),
                },

                {
                  label: 'Até',
                  value: RecurrenceTypes.UNTIL,
                  labelComponent: (
                    <div className="ml-auto">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Datepicker
                          className="mr-0"
                          labelOut={false}
                          name="until"
                          label="Data Término"
                          disablePast
                          disabled={recurrenceType !== RecurrenceTypes.UNTIL}
                          rules={eventRules.dateOpcional}
                        />
                      </LocalizationProvider>
                    </div>
                  ),
                },
              ]}
            />

            <div className="flex gap-12 justify-center pt-4">
              <Button
                size="small"
                circular
                onClick={() => {
                  setSelectedOption('Não se repete');
                  form.resetField('weekdays');
                  form.resetField('recurrenceType');
                  form.resetField('count');
                  form.resetField('until');
                  setOpenRecurrence(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={!isFormValid()}
                size="small"
                circular
                onClick={() => {
                  setSelectedOption('Valores escolhidos pelo usuário');
                  setOpenRecurrence(false);
                }}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
